import { dataLayerPush } from "./utils";
var VIMEO_IFRAME_SELECTOR = "iframe[src*=\"player.vimeo.com\"]";
var VIMEO_SDK_SRC = "https://player.vimeo.com/api/player.js";
var TRACKED_ATTR = "data-wvus-vimeo-tracked";
var PROGRESS_MARKERS = [10, 25, 50, 75, 95];
// Vimeo doesn't guarantee percent is exactly 1 on the pause it emits at
// video end, so treat anything this close to the end as end-of-video
var END_OF_VIDEO_PERCENT = 0.99;
var sdkPromise = null;
// Test-only: clears the cached SDK loader promise between test runs
export function resetVimeoSdkCacheForTesting() {
    sdkPromise = null;
}
function loadVimeoPlayerSdk() {
    var _a;
    if ((_a = globalThis.Vimeo) === null || _a === void 0 ? void 0 : _a.Player) {
        return Promise.resolve(globalThis.Vimeo.Player);
    }
    if (!sdkPromise) {
        sdkPromise = new Promise(function (resolve, reject) {
            var script = document.createElement("script");
            script.src = VIMEO_SDK_SRC;
            script.async = true;
            script.onload = function () {
                var _a;
                if ((_a = globalThis.Vimeo) === null || _a === void 0 ? void 0 : _a.Player) {
                    resolve(globalThis.Vimeo.Player);
                }
                else {
                    reject(new Error("Vimeo Player SDK loaded but unavailable"));
                }
            };
            script.onerror = function () {
                sdkPromise = null;
                reject(new Error("Failed to load Vimeo Player SDK"));
            };
            document.head.appendChild(script);
        });
    }
    return sdkPromise;
}
function isAutoplayIframe(iframe) {
    // background=1 embeds are muted, looping autoplay players
    return /[?&](autoplay|background)=(1|true)/.test(iframe.src);
}
function pushVideoEvent(action, metaData, data) {
    var _a;
    var title = metaData.title, url = metaData.url;
    var duration = (data === null || data === void 0 ? void 0 : data.duration) || metaData.duration || 0;
    var seconds = (_a = data === null || data === void 0 ? void 0 : data.seconds) !== null && _a !== void 0 ? _a : 0;
    dataLayerPush({
        event: "video_interactions",
        event_action: action,
        event_category: "video_interactions",
        event_label: title,
        video_action: action,
        video_length: duration,
        video_name: title,
        video_player: "vimeo",
        video_time: seconds,
        video_title: title,
        video_url: url,
    });
}
function addPlayerTracking(player, iframe) {
    var metaPromise = Promise.all([
        player.getVideoTitle(),
        player.getVideoUrl(),
        player.getDuration(),
    ]).then(function (_a) {
        var title = _a[0], url = _a[1], duration = _a[2];
        return ({
            title: title || window.document.title,
            url: url || iframe.src,
            duration: duration || 0,
        });
    }).catch(function () { return ({
        title: window.document.title,
        url: iframe.src,
        duration: 0,
    }); });
    var autoplayEnabled = isAutoplayIframe(iframe);
    var playCount = 0;
    var progressTracked = {};
    player.on("play", function (data) {
        // Only the first play of an autoplaying video counts as "auto play",
        // once per video per page load; every other play is a manual "play"
        var action = autoplayEnabled && playCount === 0 ? "auto play" : "play";
        playCount++;
        metaPromise.then(function (metaData) { return pushVideoEvent(action, metaData, data); });
    });
    player.on("pause", function (data) {
        var _a;
        // Vimeo emits a pause alongside ended; "Watch to End" covers that case
        if (((_a = data === null || data === void 0 ? void 0 : data.percent) !== null && _a !== void 0 ? _a : 0) >= END_OF_VIDEO_PERCENT) {
            return;
        }
        metaPromise.then(function (metaData) { return pushVideoEvent("pause", metaData, data); });
    });
    player.on("ended", function (data) {
        metaPromise.then(function (metaData) { return pushVideoEvent("Watch to End", metaData, data); });
    });
    player.on("timeupdate", function (data) {
        var _a;
        var percent = ((_a = data === null || data === void 0 ? void 0 : data.percent) !== null && _a !== void 0 ? _a : 0) * 100;
        PROGRESS_MARKERS.forEach(function (marker) {
            if (percent >= marker && !progressTracked[marker]) {
                progressTracked[marker] = true;
                metaPromise.then(function (metaData) { return pushVideoEvent("".concat(marker, "% Progress"), metaData, data); });
            }
        });
    });
}
function trackVimeoIframe(iframe) {
    if (iframe.hasAttribute(TRACKED_ATTR)) {
        return;
    }
    // Watch card videos ([data-vimeo-id] containers) run their own tracking
    if (iframe.closest("[data-vimeo-id]")) {
        return;
    }
    iframe.setAttribute(TRACKED_ATTR, "true");
    loadVimeoPlayerSdk()
        .then(function (Player) {
        addPlayerTracking(new Player(iframe), iframe);
    })
        .catch(function (err) {
        // Unmark the iframe so a later scan can retry after a failed SDK load
        iframe.removeAttribute(TRACKED_ATTR);
        console.error(err);
    });
}
function scanForVimeoIframes(root) {
    if (root === void 0) { root = document; }
    root.querySelectorAll(VIMEO_IFRAME_SELECTOR).forEach(function (iframe) {
        trackVimeoIframe(iframe);
    });
}
function observeAddedVimeoIframes() {
    // Vimeo iframes can appear at any time (e.g. the universal video widget
    // builds its player on click), so watch for additions for the page's
    // lifetime. src is also observed to catch lazy loaders that insert a
    // placeholder iframe and set the real URL later.
    var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
            if (mutation.type === "attributes") {
                var target = mutation.target;
                if (target instanceof Element && target.matches(VIMEO_IFRAME_SELECTOR)) {
                    trackVimeoIframe(target);
                }
                return;
            }
            mutation.addedNodes.forEach(function (node) {
                if (!(node instanceof Element)) {
                    return;
                }
                if (node.matches(VIMEO_IFRAME_SELECTOR)) {
                    trackVimeoIframe(node);
                }
                else {
                    scanForVimeoIframes(node);
                }
            });
        });
    });
    observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["src"],
    });
    return observer;
}
export function initVimeoVideoTracking() {
    var observer = null;
    var start = function () {
        scanForVimeoIframes();
        observer = observeAddedVimeoIframes();
    };
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", start);
    }
    else {
        start();
    }
    // Cleanup handle; production entry points ignore it, tests use it
    return function () {
        document.removeEventListener("DOMContentLoaded", start);
        observer === null || observer === void 0 ? void 0 : observer.disconnect();
        observer = null;
    };
}
